import { Circle, InfoWindow, Polyline } from "@react-google-maps/api";
import React, { useMemo, useState } from "react";
import MapContainer from "../MapContainer";
import AdvancedMarker from "../AdvancedMarker";
import dayjs from "dayjs";
import startIcon from "../../images/icons/pin-start.svg"
import finishIcon from "../../images/icons/pin-stop.svg"
import pauseIcon from "../../images/icons/pin-pause.svg"
import resumeIcon from "../../images/icons/pin-resume.svg"
import skippedIcon from "../../images/icons/pin-skipped.svg"
import homeIcon from "../../images/icons/homeIcon.png"
import workIcon from "../../images/icons/workIcon.png"
import { partialType } from "../../constants/partialType";
import OrganizationAreaLayer from "./OrganizationAreaLayer";
import { LEG_COLOR, LEG_TYPE, useGetLegTypeTranslation } from "../../constants/legType";
import MapLegend from "../MapLegend";
import { useTranslation } from "react-i18next";
import ColoredLabel from "../ColoredLabel";
import { Box } from "@mui/material";
import { FiberManualRecord } from "@mui/icons-material";
import { generateDistinctColor } from "../../utils/colorGenerator";

const StyledPolyline = ({ path, color }) => {
    return <Polyline path={path} options={{ strokeColor: color }} />
}

export default function MapSession({
    polylines,
    zoom,
    points,
    polylineType,
    homePoints,
    workPoints,
    isShowingPartialPoints,
    sessionPointsForArea,
    version,
    legs,
    isLayerVisible,
    cyclePaths,
    color
}) {

    let [infoWindow, setInfoWindow] = useState(null)
    let [homeAddressInfoWindow, setHomeAddressInfoWindow] = useState(null);
    const { t } = useTranslation();
    const legTypeTranslation = useGetLegTypeTranslation();



    const getPolylinePaths = (polylines, type) => {
        return {
            type: type,
            polyline: polylines.polyline && polylines.polyline.map(p => window.google.maps.geometry.encoding.decodePath(p)),
            rawPolyline: polylines.rawPolyline && polylines.rawPolyline.map(p => window.google.maps.geometry.encoding.decodePath(p)),
            gmapsPolyline: polylines.gmapsPolyline && polylines.gmapsPolyline.map(p => window.google.maps.geometry.encoding.decodePath(p))
        }
    }

    /*path=[
        {polyline:[
            {//gmapPolyline}
        ]}
    ]*/
    let paths = useMemo(() => {
        let paths = [];
        if (version === 2) {
            legs?.forEach(leg => {
                paths.push(getPolylinePaths(leg, leg.type));
            });
        } else {
            paths = [
                getPolylinePaths(polylines, LEG_TYPE.NONE)
            ];
        }
        return paths;
    }, [legs, polylines, version]);

    const center = useMemo(() => paths && paths[0]?.polyline ? paths[0].polyline[0][0] : undefined,
        [paths]
    );

    // Una sessione può appartenere a più iniziative (organizations) le cui aree
    // si sovrappongono sulla mappa. Assegno a ogni iniziativa un colore distinto
    // e deterministico, così la stessa iniziativa ha sempre la stessa tinta, e
    // costruisco la legenda colore -> nome iniziativa.
    const organizationAreas = useMemo(() => {
        const seen = new Map();
        (sessionPointsForArea || []).forEach(sp => {
            if (!seen.has(sp.organizationId)) {
                seen.set(sp.organizationId, sp.organizationTitle);
            }
        });
        return Array.from(seen.entries()).map(([organizationId, organizationTitle], index) => ({
            organizationId,
            organizationTitle,
            color: generateDistinctColor(index),
        }));
    }, [sessionPointsForArea]);

    const dottedLineSymbol = {
        path: "M 0,-1 0,1",
        strokeOpacity: 0.5,
        scale: 2.5,
    };

    let tolerances = [];
    homePoints && homePoints.map(hp => {
        let tol = tolerances.findIndex(t => t.tolerance === hp.tolerance)
        if (tol !== -1) {
            tolerances[tol].organizations = tolerances[tol].organizations + " - \"" + hp.organization + "\""
        } else {
            tolerances.push({ tolerance: hp.tolerance, lat: hp.lat, lng: hp.lng, organizations: "\"" + hp.organization + "\"" })
        }
    })
    tolerances.sort((a, b) => b.tolerance - a.tolerance)

    const getPausePath = (path, index, paths) => {
        if (paths[index + 1]) {
            const dottedPath = [path[path.length - 1], paths[index + 1][0]]
            return <Polyline path={dottedPath}
                options={{
                    strokeOpacity: 0,
                    icons: [
                        {
                            icon: dottedLineSymbol,
                            offset: "0",
                            repeat: "13px",
                        },
                    ],
                }} />
        }
    }

    const getPolyline = () => {
        return (
            <div>
                {
                    /*path=[
                        {polyline:[
                            {}
                        ]}
                    ]*/
                    paths.map((p, index) => {
                        //polylineType è o polyline o rawPolyline o gmapsPolyline
                        //mostro la polyline del tipo definito da polylineType
                        let path = p[polylineType]
                        if (polylineType === 'rawPolyline' && !p[polylineType]) {//rowpolyline viene ricreata se non esiste
                            let newPath = []
                            points = points.slice().sort((a, b) => a.timestamp - b.timestamp);
                            points.forEach(p => {
                                newPath.push({ lat: p.latitude, lng: p.longitude })
                            })
                            return <StyledPolyline path={newPath} color={LEG_COLOR[p.type]} />
                        }
                        return (
                            path &&
                            <div key={index}>
                                {
                                    path.map(
                                        (innerPath, i) => {
                                            return (
                                                <div key={i}>
                                                    <StyledPolyline path={innerPath} color={LEG_COLOR[p.type]} />
                                                    {getPausePath(innerPath, i, path)}
                                                </div>
                                            )
                                        }
                                    )
                                }
                            </div>
                        )
                    })
                }
            </div>
        )
    }

    return (
        <>
            {version === 2 &&
                <MapLegend
                    legendTitle={t('legend')}
                    labels={
                        Object.values(LEG_TYPE).filter(t => t !== -1).map(legType => ({
                            title: <ColoredLabel
                                color={LEG_COLOR[legType]}
                                label={legTypeTranslation[legType]}
                            />
                        })
                        )
                    }
                ></MapLegend>
            }

            {/* Legenda aree iniziativa in stile open data (card bianca in basso a sinistra):
                un colore distinto per ogni iniziativa, con il nome dell'organization */}
            {organizationAreas.length > 0 &&
                <Box sx={{
                    position: 'absolute',
                    bottom: 10,
                    left: 10,
                    zIndex: 1000,
                    backgroundColor: '#fff',
                    borderRadius: '5px',
                    boxShadow: '0 0 10px rgba(0,0,0,0.2)',
                    p: '10px',
                    maxWidth: '260px',
                }}>
                    <Box sx={{ fontWeight: 'bold', mb: '6px', fontSize: 13 }}>
                        {t('initiativeArea')}
                    </Box>
                    {organizationAreas.map(area =>
                        <Box key={area.organizationId} sx={{ display: 'flex', alignItems: 'flex-start', my: '2px' }}>
                            <FiberManualRecord sx={{ color: area.color, fontSize: 16, mr: 1, mt: '2px', flexShrink: 0 }} />
                            <Box component="span" sx={{ fontSize: 13 }}>{area.organizationTitle}</Box>
                        </Box>
                    )}
                </Box>
            }
            <MapContainer zoom={zoom} center={center} isLayerVisible={isLayerVisible} cyclePaths={cyclePaths} color={color}>

                {getPolyline()}

                {points && points.map((p, index) => {
                    let icon = null
                    let zIndex = 0
                    if (p.type === partialType.START) {
                        icon = { url: startIcon, scaledSize: new window.google.maps.Size(35, 48) }
                        zIndex = 1000
                    }
                    if (p.type === partialType.END) {
                        icon = { url: finishIcon, scaledSize: new window.google.maps.Size(35, 48) }
                        zIndex = 1000
                    }
                    if (p.type === partialType.PAUSE) {
                        icon = { url: pauseIcon, scaledSize: new window.google.maps.Size(35, 48) }
                        zIndex = 999
                    }
                    if (p.type === partialType.RESUME) {
                        icon = { url: resumeIcon, scaledSize: new window.google.maps.Size(35, 48) }
                        zIndex = 999
                    }
                    if (p.type === partialType.SKIPPED) {
                        icon = { url: skippedIcon, scaledSize: new window.google.maps.Size(30, 42) }
                        zIndex = -1
                    }

                    if (isShowingPartialPoints || zIndex > 0)
                        return <AdvancedMarker key={index} position={{ lat: p.latitude, lng: p.longitude }} onClick={() => setInfoWindow(index)}
                            icon={icon} zIndex={zIndex}>

                            {infoWindow && infoWindow === index &&
                                <InfoWindow onCloseClick={() => setInfoWindow(null)}>
                                    <div>{dayjs(new Date(p.timestamp)).format("HH:mm:ss DD/MM/YYYY")}</div>
                                </InfoWindow>}

                        </AdvancedMarker>
                })}

                {
                    homePoints && tolerances.length !== 0 && tolerances.map((point, index) => {
                        return <div>

                            <Circle center={point} radius={point.tolerance}
                                onClick={(e) => setHomeAddressInfoWindow({ lat: e.latLng.lat(), lng: e.latLng.lng(), tolerance: point.tolerance })} />

                            {homeAddressInfoWindow && homeAddressInfoWindow.tolerance === point.tolerance &&
                                <InfoWindow onCloseClick={() => setHomeAddressInfoWindow(null)} position={homeAddressInfoWindow}>
                                    <div>{point.organizations}</div>
                                </InfoWindow>
                            }

                            <AdvancedMarker key={index} position={point} icon={{ url: homeIcon, scaledSize: new window.google.maps.Size(48, 48) }} zIndex={999} />
                        </div>
                    })

                }

                {
                    workPoints && workPoints.map((point, index) => {
                        return <div>
                            <Circle center={point} radius={point.tolerance} />
                            <AdvancedMarker key={index} position={point} icon={{ url: workIcon, scaledSize: new window.google.maps.Size(48, 48) }} zIndex={999} />
                        </div>
                    })
                }

                {
                    organizationAreas.map(area => {
                        return <OrganizationAreaLayer
                            key={area.organizationId}
                            organizationId={area.organizationId}
                            color={area.color}
                        />
                    })
                }

            </MapContainer>
        </>)
}
